import React, { type HTMLAttributes } from 'react';
import cx from 'classnames';
import ButtonOrLink from '../ButtonOrLink';

import stylesheet from './MenuItem.css';

export type MenuItemProps = {
  /** `children` will not be supported in the long term.
   * Default to using the `label` prop. */
  children?: React.ReactNode;
  /** Use destructive for actions we want users to be acutely aware due to their destructive nature. Typically, there should only be one per menu. */
  /** Use group titles to group like items inside a menu */
  variant?: 'default' | 'destructive' | 'title';
  /** Divider adds a line above the item. Divider lines separate sections of your menu to increase scannability. */
  divider?: boolean;
  /** Actions that are redirected as links */
  href?: string;
  /** Icon in front of the label */
  prefix?: React.ReactNode;
  /** Icon at the end of the label */
  suffix?: React.ReactNode;
  /** Title of each action item */
  label: string;
  /** Whether the item is disabled from interaction*/
  disabled?: boolean;
  /** Mouse click event */
  onClick?:
    | (() => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void);
} & Omit<HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'prefix'>;

export type MenuItemActionProps = {
  /** Divider adds a line above the item. Divider lines separate sections of your menu to increase scannability. */
  divider?: boolean;
  /** Actions that are redirected as links */
  href?: string;
  /** Icon in front of the label */
  prefix?: React.ReactNode;
  /** Title of each action item */
  label: string;
  /** Whether the item is disabled from interaction*/
  disabled?: boolean;
  /** Mouse click event */
  onClick?:
    | (() => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void);
} & Omit<HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'prefix'>;

const MenuItem = ({
  label,
  prefix,
  suffix,
  onClick,
  href,
  variant,
  divider,
  disabled,
  ...rest
}: MenuItemProps) => {
  return (
    <>
      {divider && <div className={stylesheet.divider}></div>}
      <div
        className={cx(
          stylesheet.item,
          !onClick && !href ? stylesheet.noEvent : null,
          disabled && stylesheet.disabled,
        )}
      >
        {onClick || href ? (
          <ButtonOrLink
            onClick={onClick}
            disabled={disabled}
            className={cx(
              stylesheet.label,
              variant === 'destructive'
                ? stylesheet.destructive
                : variant === 'title'
                ? stylesheet.title
                : null,
            )}
            href={href}
            {...rest}
          >
            <div className={stylesheet.labelPrimary}>
              {prefix} {label}
            </div>
            {suffix}
          </ButtonOrLink>
        ) : (
          <div
            className={cx(
              stylesheet.label,
              variant === 'destructive'
                ? stylesheet.destructive
                : variant === 'title'
                ? stylesheet.title
                : null,
            )}
          >
            <div className={stylesheet.labelPrimary}>
              {prefix} {label ? label : null}
            </div>
            {suffix}
          </div>
        )}
      </div>
    </>
  );
};

const MenuItemAction = ({
  label,
  onClick,
  href,
  prefix,
  disabled,
  ...rest
}: MenuItemActionProps) => {
  return (
    <div
      className={cx(
        stylesheet.item,
        stylesheet.itemAction,
        disabled && stylesheet.disabled,
      )}
    >
      <ButtonOrLink
        onClick={onClick}
        disabled={disabled}
        className={stylesheet.label}
        href={href}
        {...rest}
      >
        <div className={stylesheet.labelAction}>
          {prefix}
          {label}
        </div>
      </ButtonOrLink>
    </div>
  );
};

export default MenuItem;
export { MenuItemAction };
